import { integrationApi } from "@repo/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { getCronExpression } from "../../utils/schedule";

/** Same API as GCP Vertex: POST /integrationProvisioning/createEndpoint (via createIntegration). */
const DEFAULT_INTEGRATION_TYPE = "CopilotStudio";

interface UseCreateCopilotStudioIntegrationProps {
  onSuccess: (
    message: string,
    isDraft?: boolean,
    integrationType?: string,
  ) => void;
  onError: (message: string) => void;
}

export function useCreateCopilotStudioIntegration({
  onSuccess,
  onError,
}: UseCreateCopilotStudioIntegrationProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      isEnabled,
      data,
    }: {
      isEnabled: boolean;
      data: Record<string, unknown>;
    }) => {
      const payload = {
        displayName: (data.name as string) || "",
        isEnabled,
        credentials: {
          type: "OAUTH2",
          data: {
            clientTenantId: (data.tenantId as string) || "",
            clientId: (data.clientId as string) || "",
            clientSecret: (data.clientSecret as string) || "",
            dataverseUrl: (data.dataverseUrl as string) || "",
          },
          metadata: {},
        },
        metadata: {
          accountId: (data.dataverseUrl as string) || "",
          description: (data.description as string) ?? "",
        },
        features: ((data.featureNames as string[]) || []).map(
          (featureName) => ({
            featureName,
            schedules: [
              {
                scheduleName: "copilotstudio-schedule",
                cronExpression: getCronExpression(
                  (data.frequency as string) || "24hours",
                  data.importTime as Date | null,
                ),
              },
            ],
          }),
        ),
        integrationType:
          (data.integrationType as string) || DEFAULT_INTEGRATION_TYPE,
      };
      const type = (data.integrationType as string) || DEFAULT_INTEGRATION_TYPE;
      return integrationApi.createIntegration(type, payload);
    },
    onMutate: async ({ isEnabled, data }) => {
      await queryClient.cancelQueries({ queryKey: ["integrations"] });
      const previousIntegrations = queryClient.getQueryData(["integrations"]);

      queryClient.setQueryData(["integrations"], (old: unknown) => {
        const oldData = old as
          | { integrations?: Array<Record<string, unknown>> }
          | undefined;
        const newIntegration = {
          id: Date.now(),
          displayName: (data.name as string) || "",
          description: data.description as string,
          integrationType:
            (data.integrationType as string) || DEFAULT_INTEGRATION_TYPE,
          connectionStatus: "PENDING",
          isEnabled,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        return {
          ...oldData,
          integrations: [newIntegration, ...(oldData?.integrations || [])],
        };
      });

      return { previousIntegrations };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousIntegrations) {
        queryClient.setQueryData(
          ["integrations"],
          context.previousIntegrations,
        );
      }
      onError(
        intl.formatMessage({
          id: "l8gxpc",
          defaultMessage: "Failed to create integration",
          description: "Error message for integration creation failure",
        }),
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["integrations"] });
      const isDraft = !variables.isEnabled;
      if (isDraft) {
        onSuccess(
          intl.formatMessage(
            {
              id: "c2bz4O",
              defaultMessage:
                "You can resume the setup of {integrationType} at any time.",
              description: "Draft save success message",
            },
            {
              integrationType: variables.data.name as string,
            },
          ),
          true,
          variables.data.name as string,
        );
      } else {
        onSuccess(
          intl.formatMessage({
            id: "gRl5vB",
            defaultMessage: "Integration created successfully",
            description: "Success message for integration creation",
          }),
          false,
        );
      }
    },
  });
}
